import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect } from 'react';
import './PropertyWizard.css';

function ListingEditModal({ listing, onClose, onSaved }) {
  const isSale = listing.listing_type === 'SALE';
  const token = () => localStorage.getItem('access_token');

  // SALE fields
  const [askingPrice, setAskingPrice] = useState(listing.sale_details?.asking_price ?? '');
  const [mortgageStatus, setMortgageStatus] = useState(listing.sale_details?.mortgage_status ?? 'NONE');
  const [mortgageTotalAmount, setMortgageTotalAmount] = useState(listing.sale_details?.mortgage_total_amount ?? '');
  const [mortgageRepayPeriod, setMortgageRepayPeriod] = useState(listing.sale_details?.mortgage_repay_period ?? '');
  const [mortgagePayPerMonth, setMortgagePayPerMonth] = useState(listing.sale_details?.mortgage_pay_per_month ?? '');
  const [hasTenant, setHasTenant] = useState(listing.sale_details?.has_tenant ?? false);
  const [tenantDeposit, setTenantDeposit] = useState(listing.sale_details?.tenant_deposit ?? '');
  const [tenantMonthlyRent, setTenantMonthlyRent] = useState(listing.sale_details?.tenant_monthly_rent ?? '');

  // RENT fields
  const [idealDeposit, setIdealDeposit] = useState(listing.rent_details?.ideal_deposit ?? '');
  const [idealRent, setIdealRent] = useState(listing.rent_details?.ideal_rent ?? '');
  const [minRent, setMinRent] = useState(listing.rent_details?.min_rent ?? '');
  const [minDeposit, setMinDeposit] = useState(listing.rent_details?.min_deposit ?? '');
  const [totalCost, setTotalCost] = useState(listing.rent_details?.total_cost ?? '');
  const [convertible, setConvertible] = useState(listing.rent_details?.convertible ?? false);
  const [leaseTermMonths, setLeaseTermMonths] = useState(listing.rent_details?.lease_term_months ?? 12);
  const [furnishedStatus, setFurnishedStatus] = useState(listing.rent_details?.furnished_status ?? 'UNFURNISHED');

  // Schedule
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const DAY_LABELS = { 0: 'دوشنبه', 1: 'سه‌شنبه', 2: 'چهارشنبه', 3: 'پنج‌شنبه', 4: 'جمعه', 5: 'شنبه', 6: 'یکشنبه' };
  const TIME_SLOTS = ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];
  const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const res = await apiRequest(apiRoutes.listings.schedules(listing.id),
        { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setSchedules(Array.isArray(data) ? data : data.results || []);
      }
    } catch { /* non-fatal */ }
    setLoadingSchedules(false);
  };

  const isSlotSelected = (weekday, start) =>
    schedules.some(s => s.weekday === weekday && s.start_time === start + ':00');

  const toggleSlot = async (weekday, start) => {
    const end = TIME_SLOTS[TIME_SLOTS.indexOf(start) + 1];
    if (!end) return;
    const existing = schedules.find(s => s.weekday === weekday && s.start_time === start + ':00');
    if (existing) {
      await apiRequest(apiRoutes.listings.schedule(listing.id, existing.id),
        { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    } else {
      await apiRequest(apiRoutes.listings.schedules(listing.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ weekday, start_time: start, end_time: end })
      });
    }
    await fetchSchedules();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const endpoint = isSale
        ? apiRoutes.listings.saleDetails(listing.id)
        : apiRoutes.listings.rentDetails(listing.id);

      const body = isSale
        ? {
            asking_price: askingPrice || null,
            mortgage_status: mortgageStatus,
            mortgage_total_amount: mortgageTotalAmount || null,
            mortgage_repay_period: mortgageRepayPeriod || null,
            mortgage_pay_per_month: mortgagePayPerMonth || null,
            has_tenant: hasTenant,
            tenant_deposit: hasTenant ? (tenantDeposit || null) : null,
            tenant_monthly_rent: hasTenant ? (tenantMonthlyRent || null) : null,
          }
        : {
            ideal_deposit: idealDeposit || null,
            ideal_rent: idealRent || null,
            min_rent: minRent || null,
            min_deposit: minDeposit || null,
            total_cost: totalCost || null,
            convertible,
            lease_term_months: leaseTermMonths || 12,
            furnished_status: furnishedStatus,
          };

      const res = await apiRequest(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || JSON.stringify(err));
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content listing-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isSale ? 'ویرایش آگهی فروش' : 'ویرایش آگهی اجاره'}</h3>
          <button className="modal-close" onClick={onClose}>بستن</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="edit-section">
            <h4 className="edit-section-title"> قیمت و شرایط</h4>

            {isSale ? (
              <>
                <div className="field-group">
                  <label>قیمت فروش (تومان)</label>
                  <input type="number" className="text-input" value={askingPrice}
                    onChange={e => setAskingPrice(e.target.value)} placeholder="قیمت درخواستی" />
                </div>

                <div className="field-group">
                  <label>وضعیت وام</label>
                  <div className="option-group">
                    {['NONE', 'AVAILABLE'].map(v => (
                      <label key={v} className={`option-item ${mortgageStatus === v ? 'selected' : ''}`}>
                        <input type="radio" checked={mortgageStatus === v} onChange={() => setMortgageStatus(v)} />
                        <span>{v === 'NONE' ? 'ندارد' : 'دارد'}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {mortgageStatus === 'AVAILABLE' && (
                  <>
                    <div className="field-group">
                      <label>مبلغ کل وام (تومان)</label>
                      <input type="number" className="text-input" value={mortgageTotalAmount}
                        onChange={e => setMortgageTotalAmount(e.target.value)} placeholder="مبلغ کل وام" />
                    </div>
                    <div className="field-group">
                      <label>مدت بازپرداخت (ماه)</label>
                      <input type="number" className="text-input" value={mortgageRepayPeriod}
                        onChange={e => setMortgageRepayPeriod(e.target.value)} placeholder="تعداد ماه" />
                    </div>
                    <div className="field-group">
                      <label>قسط ماهانه (تومان)</label>
                      <input type="number" className="text-input" value={mortgagePayPerMonth}
                        onChange={e => setMortgagePayPerMonth(e.target.value)} placeholder="مبلغ قسط" />
                    </div>
                  </>
                )}

                <div className="field-group">
                  <label>وضعیت مستاجر</label>
                  <div className="option-group">
                    <label className={`option-item ${!hasTenant ? 'selected' : ''}`}>
                      <input type="radio" checked={!hasTenant} onChange={() => setHasTenant(false)} />
                      <span>خالی</span>
                    </label>
                    <label className={`option-item ${hasTenant ? 'selected' : ''}`}>
                      <input type="radio" checked={hasTenant} onChange={() => setHasTenant(true)} />
                      <span>دارای مستاجر</span>
                    </label>
                  </div>
                </div>

                {hasTenant && (
                  <>
                    <div className="field-group">
                      <label>ودیعه مستاجر فعلی (تومان)</label>
                      <input type="number" className="text-input" value={tenantDeposit}
                        onChange={e => setTenantDeposit(e.target.value)} placeholder="مبلغ ودیعه" />
                    </div>
                    <div className="field-group">
                      <label>اجاره ماهانه مستاجر فعلی (تومان)</label>
                      <input type="number" className="text-input" value={tenantMonthlyRent}
                        onChange={e => setTenantMonthlyRent(e.target.value)} placeholder="مبلغ اجاره" />
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="field-group">
                  <label>ودیعه ایده‌آل (تومان)</label>
                  <input type="number" className="text-input" value={idealDeposit}
                    onChange={e => setIdealDeposit(e.target.value)} placeholder="ودیعه ایده‌آل" />
                </div>
                <div className="field-group">
                  <label>اجاره ایده‌آل ماهانه (تومان)</label>
                  <input type="number" className="text-input" value={idealRent}
                    onChange={e => setIdealRent(e.target.value)} placeholder="اجاره ایده‌آل" />
                </div>
                <div className="field-group">
                  <label>حداقل اجاره ماهانه (تومان)</label>
                  <input type="number" className="text-input" value={minRent}
                    onChange={e => setMinRent(e.target.value)} placeholder="مبلغ اجاره" />
                </div>
                <div className="field-group">
                  <label>حداقل ودیعه (تومان)</label>
                  <input type="number" className="text-input" value={minDeposit}
                    onChange={e => setMinDeposit(e.target.value)} placeholder="مبلغ رهن" />
                </div>
                <div className="field-group">
                  <label>هزینه کل (تومان)</label>
                  <input type="number" className="text-input" value={totalCost}
                    onChange={e => setTotalCost(e.target.value)} placeholder="ودیعه ایده‌آل + اجاره" />
                </div>
                <div className="field-group">
                  <label>مدت قرارداد (ماه)</label>
                  <input type="number" className="text-input" value={leaseTermMonths}
                    onChange={e => setLeaseTermMonths(e.target.value)} placeholder="حداقل مدت اجاره" />
                </div>
                <div className="field-group">
                  <label>قابل تبدیل (رهن و اجاره)</label>
                  <div className="option-group">
                    <label className={`option-item ${!convertible ? 'selected' : ''}`}>
                      <input type="radio" checked={!convertible} onChange={() => setConvertible(false)} />
                      <span>خیر</span>
                    </label>
                    <label className={`option-item ${convertible ? 'selected' : ''}`}>
                      <input type="radio" checked={convertible} onChange={() => setConvertible(true)} />
                      <span>بله</span>
                    </label>
                  </div>
                </div>
                <div className="field-group">
                  <label>وضعیت مبلمان</label>
                  <div className="option-group">
                    {[['UNFURNISHED', 'بدون مبلمان'], ['SEMI_FURNISHED', 'نیمه مبله'], ['FULLY_FURNISHED', 'کاملاً مبله']].map(([v, l]) => (
                      <label key={v} className={`option-item ${furnishedStatus === v ? 'selected' : ''}`}>
                        <input type="radio" checked={furnishedStatus === v} onChange={() => setFurnishedStatus(v)} />
                        <span>{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="edit-section">
            <h4 className="edit-section-title">زمان‌بندی بازدید</h4>
            {loadingSchedules ? (
              <p className="loading-text">در حال بارگذاری...</p>
            ) : (
              <div className="schedule-grid">
                <div className="schedule-header-row">
                  <div className="schedule-time-col" />
                  {WEEKDAYS.map(d => (
                    <div key={d} className="schedule-day-header">{DAY_LABELS[d]}</div>
                  ))}
                </div>
                {TIME_SLOTS.slice(0, -1).map((start, i) => (
                  <div key={start} className="schedule-row">
                    <div className="schedule-time-col">{start}–{TIME_SLOTS[i + 1]}</div>
                    {WEEKDAYS.map(d => (
                      <div
                        key={d}
                        className={`schedule-cell ${isSlotSelected(d, start) ? 'selected' : ''}`}
                        onClick={() => toggleSlot(d, start)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-button" onClick={onClose}>انصراف</button>
          <button className="modal-submit-button" onClick={handleSave} disabled={saving}>
            {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ListingEditModal;
